<template>
  <v-container fluid class="workshop-home pa-3 pa-md-4">
    <div class="d-flex flex-wrap align-center ga-2 mb-3">
      <h1 class="text-h6 text-md-h5 flex-grow-1">
        {{ $t("workshop.home.title") }}
      </h1>
      <v-btn
        class="d-none d-md-flex"
        color="primary"
        prepend-icon="mdi-qrcode-scan"
        @click="openScanner"
      >
        {{ $t("workshop.home.scan") }}
      </v-btn>
    </div>

    <v-btn
      class="d-md-none mb-3"
      block
      size="x-large"
      color="primary"
      prepend-icon="mdi-qrcode-scan"
      @click="openScanner"
    >
      {{ $t("workshop.home.scan") }}
    </v-btn>

    <v-alert
      v-if="cameraUnavailable"
      type="info"
      variant="tonal"
      closable
      class="mb-3"
      @click:close="cameraUnavailable = false"
    >
      {{ $t("workshop.scanner.camera_unavailable") }}
    </v-alert>

    <v-text-field
      ref="search"
      v-model="search"
      :label="$t('workshop.home.search_label')"
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      density="comfortable"
      clearable
      hide-details
      :maxlength="queryMax"
      class="mb-3"
    />

    <!-- The chip row scrolls sideways on its own; the page never does. -->
    <div class="workshop-filters mb-3">
      <v-chip
        v-for="chip in chips"
        :key="chip.state"
        :color="chip.color"
        :variant="chip.state === state ? 'flat' : 'outlined'"
        class="mr-2"
        @click="selectFilter(chip.state)"
      >
        {{ $t(chip.key) }} · {{ chip.count }}
      </v-chip>
    </div>

    <div v-if="loading">
      <v-skeleton-loader
        v-for="n in 3"
        :key="n"
        type="list-item-two-line"
        class="mb-2"
      />
    </div>

    <v-alert v-else-if="error" type="error" variant="tonal">
      <div class="d-flex flex-wrap align-center ga-2">
        <span class="flex-grow-1">{{ $t("workshop.home.load_error") }}</span>
        <v-btn variant="outlined" size="small" @click="load">
          {{ $t("workshop.home.retry") }}
        </v-btn>
      </div>
    </v-alert>

    <div
      v-else-if="!rows.length"
      class="text-body-1 text-medium-emphasis text-center py-8"
    >
      {{ $t(emptyText) }}
    </div>

    <template v-else>
      <!-- Phone: one card per checklist. -->
      <div class="d-md-none">
        <v-card
          v-for="row in rows"
          :key="row.checklistId"
          :to="checklistRoute(row)"
          variant="outlined"
          class="mb-2"
        >
          <v-card-text>
            <div class="text-subtitle-1 font-weight-bold">
              {{ row.targa || "—" }}
            </div>
            <div v-if="row.vin" class="text-body-2 workshop-vin">
              {{ $t("workshop.home.columns.vin") }}: {{ row.vin }}
            </div>
            <div class="text-body-2 text-medium-emphasis mb-2 text-wrap">
              {{ row.vehicle }}
            </div>
            <div class="d-flex flex-wrap ga-2">
              <v-chip
                v-if="row.chip"
                size="small"
                variant="tonal"
                :color="row.chip.color"
                :title="chipTitle(row)"
              >
                {{ $t(row.chip.key, row.chip.params) }}
              </v-chip>
              <v-chip
                v-if="row.pendingExceptions > 0"
                size="small"
                variant="tonal"
                color="amber"
              >
                {{
                  $t("workshop.home.exceptions_chip", {
                    count: row.pendingExceptions,
                  })
                }}
              </v-chip>
              <v-chip
                v-if="row.deadline"
                size="small"
                variant="tonal"
                :color="row.deadline.color"
              >
                {{ $t(row.deadline.key, row.deadline.params) }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Desktop: a table that wraps long names instead of scrolling sideways. -->
      <v-table class="d-none d-md-block workshop-table" density="comfortable">
        <thead>
          <tr>
            <th>{{ $t("workshop.home.columns.targa") }}</th>
            <th>{{ $t("workshop.home.columns.vin") }}</th>
            <th>{{ $t("workshop.home.columns.vehicle") }}</th>
            <th>{{ $t("workshop.home.columns.state") }}</th>
            <th>{{ $t("workshop.home.columns.progress") }}</th>
            <th>{{ $t("workshop.home.columns.exceptions") }}</th>
            <th>
              {{ $t("workshop.home.columns.deadline") }}
              <v-icon size="small">mdi-arrow-up</v-icon>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.checklistId">
            <td class="font-weight-bold">{{ row.targa || "—" }}</td>
            <td class="workshop-vin">{{ row.vin || "—" }}</td>
            <td class="text-wrap">{{ row.vehicle }}</td>
            <td>
              <v-chip
                v-if="row.chip"
                size="small"
                variant="tonal"
                :color="row.chip.color"
                :title="chipTitle(row)"
              >
                {{ $t(row.chip.key, row.chip.params) }}
              </v-chip>
            </td>
            <td class="workshop-progress">
              <v-progress-linear
                :model-value="row.percent"
                color="primary"
                height="6"
                rounded
              />
              <div class="text-caption">{{ row.done }}/{{ row.total }}</div>
            </td>
            <td>
              {{ row.pendingExceptions > 0 ? row.pendingExceptions : "—" }}
            </td>
            <td>
              <v-chip
                v-if="row.deadline"
                size="small"
                variant="tonal"
                :color="row.deadline.color"
              >
                {{ $t(row.deadline.key, row.deadline.params) }}
              </v-chip>
              <span v-else>—</span>
            </td>
            <td class="text-right">
              <v-btn
                size="small"
                variant="outlined"
                color="primary"
                :to="checklistRoute(row)"
              >
                {{ $t("workshop.home.open") }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>

      <div class="text-caption text-medium-emphasis mt-2">
        {{ $t("workshop.home.footer", { filter: $t(selectedKey), count }) }}
      </div>

      <v-pagination
        v-if="pages > 1"
        v-model="page"
        :length="pages"
        :total-visible="5"
        density="comfortable"
        class="mt-2"
        @update:model-value="load"
      />
    </template>

    <workshop-scanner
      ref="scanner"
      @open-checklist="openChecklist"
      @camera-unavailable="onCameraUnavailable"
    />
  </v-container>
</template>

<script>
// Workshop home "Bonifica officina" (Stage 3a plan §D1): filters with counts, plate/VIN search,
// phone cards and a desktop table, and the QR scanner. The route needs the workshop switch
// (router meta requiresWorkshop); the API checks it again.
import { listWorkshopChecklists } from "@/api/compliance";
import WorkshopScanner from "@/components/Workshop/workshopScanner.vue";
import {
  DEFAULT_FILTER,
  PAGE_LIMIT,
  QUERY_MAX,
  cleanQuery,
  emptyKey,
  filterChips,
  filterKey,
  pageCount,
  rowView,
} from "@/components/Workshop/workshopList.mjs";

const SEARCH_DELAY_MS = 300;

export default {
  components: {
    WorkshopScanner,
  },
  data: () => ({
    state: DEFAULT_FILTER,
    search: "",
    query: "",
    page: 1,
    loading: true,
    error: false,
    list: { checklists: [], count: 0, counts: {} },
    requestSeq: 0,
    searchTimer: null,
    cameraUnavailable: false,
    queryMax: QUERY_MAX,
  }),
  computed: {
    chips() {
      return filterChips(this.list.counts);
    },
    rows() {
      return (this.list.checklists || []).map(rowView);
    },
    count() {
      return this.list.count || 0;
    },
    pages() {
      return pageCount(this.count, PAGE_LIMIT);
    },
    selectedKey() {
      return filterKey(this.state);
    },
    emptyText() {
      return emptyKey(this.state, this.query, this.list.counts);
    },
  },
  watch: {
    // The search keeps the selected filter; the counts follow it.
    search(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const query = cleanQuery(value);
        if (query === this.query) return;
        this.query = query;
        this.page = 1;
        this.load();
      }, SEARCH_DELAY_MS);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    // Only the latest request may fill the list.
    async load() {
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.loading = true;
      this.error = false;
      try {
        const list = await listWorkshopChecklists({
          q: this.query,
          state: this.state,
          page: this.page,
          limit: PAGE_LIMIT,
        });
        if (seq !== this.requestSeq) return;
        this.list = list;
      } catch (err) {
        if (seq !== this.requestSeq) return;
        this.error = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    selectFilter(state) {
      if (state === this.state) return;
      this.state = state;
      this.page = 1;
      this.load();
    },
    checklistRoute(row) {
      return {
        name: "workshop-checklist",
        params: { checklistId: row.checklistId },
      };
    },
    chipTitle(row) {
      const title = row.chip && row.chip.title;
      return title ? this.$t(title.key, title.params) : undefined;
    },
    openScanner() {
      this.cameraUnavailable = false;
      this.$refs.scanner.open();
    },
    openChecklist(checklistId) {
      this.$router.push(this.checklistRoute({ checklistId }));
    },
    // Camera denied or missing: say so and put the cursor in the search.
    onCameraUnavailable() {
      this.cameraUnavailable = true;
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus();
      });
    },
  },
};
</script>

<style scoped>
.workshop-home {
  max-width: 100%;
  overflow-x: hidden;
}

.workshop-filters {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  max-width: 100%;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}

.workshop-filters .v-chip {
  flex: 0 0 auto;
}

.workshop-table :deep(td),
.workshop-table :deep(th) {
  white-space: normal;
  word-break: break-word;
}

.workshop-vin {
  font-family: monospace;
  word-break: break-all;
}

.workshop-progress {
  min-width: 120px;
}
</style>
